import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'

export type CountryOption = {
  value: string
  label: string
}

export type FounderGender = 'male' | 'female'

export type FounderFormData = {
  name: string
  email: string
  gender: FounderGender | ''
  linkedin_url: string
  twitter_url: string
  github_url: string
  website_url: string
  date_of_birth: string
  country: string
  kalio_id: string | number | null
  avatar: File | null
  remove_avatar: boolean
}

type FounderFormFieldsProps<T extends FounderFormData> = {
  data: T
  errors: Partial<Record<keyof T, string>>
  setData: <K extends keyof T>(key: K, value: T[K]) => void
  countries: CountryOption[]
  existingAvatarUrl?: string | null
  avatarIsUpload?: boolean
}

export function FounderFormFields<T extends FounderFormData>({
  data,
  errors,
  setData,
  countries,
  existingAvatarUrl,
  avatarIsUpload,
}: FounderFormFieldsProps<T>) {
  const pickedPreview = data.avatar ? URL.createObjectURL(data.avatar) : null
  const previewUrl = pickedPreview ?? (data.remove_avatar ? null : existingAvatarUrl ?? null)
  const err = (key: keyof T) => errors[key]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData('name' as keyof T, e.target.value as T[keyof T])}
              autoFocus
            />
            {err('name' as keyof T) && (
              <p className="text-xs text-destructive">{err('name' as keyof T)}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => setData('email' as keyof T, e.target.value as T[keyof T])}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="date_of_birth">Date of birth</Label>
            <Input
              id="date_of_birth"
              type="date"
              value={data.date_of_birth}
              onChange={(e) =>
                setData('date_of_birth' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="country">Country</Label>
            <Select
              id="country"
              value={data.country}
              onChange={(e) => setData('country' as keyof T, e.target.value as T[keyof T])}
            >
              <option value="">—</option>
              {countries.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gender">Gender</Label>
            <Select
              id="gender"
              value={data.gender}
              onChange={(e) => setData('gender' as keyof T, e.target.value as T[keyof T])}
            >
              <option value="">—</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="kalio_id">
              Kalio ID <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="kalio_id"
              type="number"
              inputMode="numeric"
              value={data.kalio_id ?? ''}
              onChange={(e) =>
                setData('kalio_id' as keyof T, (e.target.value || null) as T[keyof T])
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Links</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="linkedin_url">LinkedIn</Label>
            <Input
              id="linkedin_url"
              type="url"
              placeholder="https://linkedin.com/in/…"
              value={data.linkedin_url}
              onChange={(e) =>
                setData('linkedin_url' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="twitter_url">X / Twitter</Label>
            <Input
              id="twitter_url"
              type="url"
              placeholder="https://x.com/…"
              value={data.twitter_url}
              onChange={(e) =>
                setData('twitter_url' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="github_url">GitHub</Label>
            <Input
              id="github_url"
              type="url"
              placeholder="https://github.com/…"
              value={data.github_url}
              onChange={(e) =>
                setData('github_url' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="website_url">Website</Label>
            <Input
              id="website_url"
              type="url"
              placeholder="https://"
              value={data.website_url}
              onChange={(e) =>
                setData('website_url' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Profile picture</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Avatar preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Input
                id="avatar"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null
                  setData('avatar' as keyof T, file as T[keyof T])
                  if (file) setData('remove_avatar' as keyof T, false as T[keyof T])
                }}
              />
              {avatarIsUpload && !data.avatar && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={data.remove_avatar}
                    onChange={(e) =>
                      setData('remove_avatar' as keyof T, e.target.checked as T[keyof T])
                    }
                  />
                  Remove uploaded picture (falls back to Gravatar)
                </label>
              )}
              {!avatarIsUpload && (
                <p className="text-xs text-muted-foreground">
                  With no upload we show a Gravatar based on the email.
                </p>
              )}
            </div>
          </div>
          {err('avatar' as keyof T) && (
            <p className="text-xs text-destructive">{err('avatar' as keyof T)}</p>
          )}
        </CardContent>
      </Card>
    </>
  )
}
